import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { apiRequest } from '../utils/api';

export const EmpresaSelectorContext = createContext();

export const useEmpresaSelector = () => {
  const context = useContext(EmpresaSelectorContext);
  if (!context) {
    throw new Error('useEmpresaSelector debe usarse dentro de un EmpresaSelectorProvider');
  }
  return context;
};

export const EmpresaSelectorProvider = ({ children }) => {
  const [empresas, setEmpresas] = useState([]);
  const [empresaSeleccionada, setEmpresaSeleccionada] = useState(null);
  const [loading, setLoading] = useState(true);
  const { usuario } = useAuth();
  const hasLoaded = useRef(false);
  const esConexAdmin = usuario?.rol === 'conexadmin';
  const puedeCambiarEmpresa = esConexAdmin;

  const cargarEmpresas = useCallback(async () => {
    if (!usuario) {
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const userRol = usuario.rol;
      const userEmpresaId = usuario.empresaId;
      if (userRol === 'conexadmin') {
        const response = await apiRequest('/empresas');
        
        if (response.ok) {
          const empresasData = await response.json();
          setEmpresas(empresasData);

          const empresaGuardada = localStorage.getItem('empresaSeleccionada');
          if (empresaGuardada) {
            try {
              const parsedEmpresa = JSON.parse(empresaGuardada);
              if (parsedEmpresa.id === 'all') {
                setEmpresaSeleccionada({ id: 'all', nombre: 'Todas las empresas' });
              } else {
                const empresa = empresasData.find(emp => String(emp.id) === String(parsedEmpresa.id));
                if (empresa) {
                  setEmpresaSeleccionada(empresa);
                } else {
                  setEmpresaSeleccionada({ id: 'all', nombre: 'Todas las empresas' });
                }
              }
            } catch (e) {
              console.error('❌ EmpresaSelectorContext: Error al parsear empresa guardada:', e);
              setEmpresaSeleccionada({ id: 'all', nombre: 'Todas las empresas' });
            }
          } else {
            setEmpresaSeleccionada({ id: 'all', nombre: 'Todas las empresas' });
          }
        } else {
          console.error('❌ EmpresaSelectorContext: Error cargando empresas:', response.status);
        }
      } else if (userEmpresaId) {
        try {
          const empresaResponse = await apiRequest(`/empresas/${userEmpresaId}`);
          if (empresaResponse.ok) {
            const empresaData = await empresaResponse.json();
            setEmpresaSeleccionada({
              id: empresaData.id,
              nombre: empresaData.nombre
            });
          } else {
            setEmpresaSeleccionada({
              id: userEmpresaId,
              nombre: 'Empresa Asignada'
            });
          }
        } catch (error) {
          console.error('❌ EmpresaSelectorContext: Error cargando empresa:', error);
          setEmpresaSeleccionada({
            id: userEmpresaId,
            nombre: 'Empresa Asignada'
          });
        }
        setEmpresas([]);
      } else {
        setEmpresaSeleccionada(null);
        setEmpresas([]);
      }
    } catch (error) {
      console.error('❌ EmpresaSelectorContext: Error cargando empresas:', error);
    } finally {
      setLoading(false);
    }
  }, [usuario?.id, usuario?.rol, usuario?.empresaId]);

  const cambiarEmpresa = useCallback((empresa) => {
    setEmpresaSeleccionada(empresa);
    if (esConexAdmin) {
      try {
        localStorage.setItem('empresaSeleccionada', JSON.stringify(empresa));
      } catch (e) {
        console.error('Error guardando empresa en localStorage:', e);
      }
    }
  }, [esConexAdmin]);

  useEffect(() => {
    if (usuario?.id) {
      hasLoaded.current = false;
      cargarEmpresas();
    } else {
      setLoading(false);
    }
  }, [usuario?.id, cargarEmpresas]);

  const value = {
    empresas,
    empresaSeleccionada,
    loading,
    puedeCambiarEmpresa,
    cambiarEmpresa,
    esConexAdmin,
    cargarEmpresas
  };

  return (
    <EmpresaSelectorContext.Provider value={value}>
      {children}
    </EmpresaSelectorContext.Provider>
  );
};
